import { useEffect, useState } from "react"
import { BrowserRouter as Router } from "react-router-dom"
import "./App.css"

import "./themes.css"

import oktaAuth from "./Model/oktaAuthSingleton"
import LeanCanvas from "./LeanCanvas/LeanCanvas"

export interface OktaUser {
    sub: string | undefined
    name: string | undefined
    locale: string | undefined
    email: string | undefined
    preferred_username: string | undefined
    given_name: string | undefined
    family_name: string | undefined
    zoneinfo: string | undefined
    updated_at: number | undefined
    email_verified: boolean | undefined
}

type AuthState = {
    isAuthenticated: boolean
    user: OktaUser | null
    isAuthenticating: boolean
}

function App() {
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        user: null,
        isAuthenticating: true,
    })
    const [needsSignInHelp, setNeedsSignInHelp] = useState(false)
    const [redirectUri, setRedirectUri] = useState<string | null>(null)

    // I'm trying to preserve the original URL from after the login redirect
    // it is currently not working
    useEffect(() => {
        if (window.location.pathname === "/auth/login/callback") {
            oktaAuth.handleLoginRedirect().then(() => {
                checkAuthentication().then(() => {
                    const stateString = oktaAuth.authStateManager.getAuthState()?.state as string

                    if (stateString) {
                        try {
                            const { fromUri } = JSON.parse(stateString)
                            setRedirectUri(fromUri) // Set state instead of immediate redirect
                        } catch (error) {
                            console.error("Error parsing state parameter:", error)
                            setRedirectUri("/")
                        }
                    } else {
                        setRedirectUri("/")
                    }
                })
            })
        } else {
            checkAuthentication()
        }
    }, [])

    useEffect(() => {
        if (redirectUri) {
            window.location.replace(redirectUri)
        }
    }, [redirectUri])

    const checkAuthentication = async () => {
        const isAuthenticated = await oktaAuth.isAuthenticated()
        if (isAuthenticated !== authState.isAuthenticated) {
            let user = null
            if (isAuthenticated) {
                try {
                    user = (await oktaAuth.getUser()) as OktaUser
                } catch (err) {
                    // handle or log the error as needed
                    console.error(err)
                }
            }
            setAuthState({ isAuthenticated, user, isAuthenticating: false })
        } else {
            setAuthState({
                isAuthenticated: false,
                user: null,
                isAuthenticating: false,
            })
        }
    }

    const login = async () => {
        // Capture the current path
        const fromUri = window.location.pathname + window.location.search
        oktaAuth.signInWithRedirect({
            // Pass the captured path in the state parameter or use Okta's built-in options if available
            state: JSON.stringify({ fromUri }),
        })
    }

    const logout = async () => {
        oktaAuth.tokenManager.clear()
        await oktaAuth.signOut()
    }

    if (authState.isAuthenticating) {
        return (
            <div className="App">
                <div>Authenticating</div>
            </div>
        )
    } else if (authState.isAuthenticated && authState.user) {
        return (
            <div>
                <SecureApp oktaUser={authState.user} onSignOut={logout} />
            </div>
        )
    } else {
        return (
            <div className="sign-in-container">
                <div className="sign-in-card">
                    <div style={{ textAlign: "center" }}>Lean Canvas Home</div>
                    <button className="sign-in-button" onClick={login}>
                        Sign In With Okta
                    </button>
                    {needsSignInHelp ? (
                        <div className="sign-in-help">
                            <div style={{ fontWeight: "bold", fontSize: "18px" }}>How to sign in:</div>
                            <div>
                                If this is your first time signing in, you will need to visit{" "}
                                <a className="help-link" href={`https://sso.apps.carrier.com`}>
                                    sso.apps.carrier.com{" "}
                                </a>{" "}
                                sso.apps.carrier.com to activate your Lean Canvas account.
                            </div>
                            <div>
                                If you are unable to sign in to sso.apps.carrier.com, we will need to make sure your email address has been added to the Lean Canvas group. Please contact{" "}
                                <a className="help-link" href={`slack://user?team=T014J7H9F0D&id=U02FR76S538`}>
                                    Service Now request SSO App - Lean Canvas{" "}
                                </a>{" "}
                                and ask him to add you.
                            </div>
                            <div>
                                If you are still unable to sign in, you can reach out to{" "}
                                <a className="help-link" href={`slack://user?team=T014J7H9F0D&id=U02HQUF02QP`}>
                                    Jason Smith{" "}
                                </a>{" "}
                                for further assistance.
                            </div>
                        </div>
                    ) : (
                        <div
                            className="sign-in-help-trigger"
                            onClick={() => {
                                setNeedsSignInHelp(true)
                            }}
                        >
                            Need help signing in?
                        </div>
                    )}
                </div>
            </div>
        )
    }
}

interface SecureAppProps {
    oktaUser: OktaUser
    onSignOut: () => void
}

const SecureApp: React.FC<SecureAppProps> = ({ oktaUser, onSignOut }) => {
    useEffect(() => {}, [])

    return (
        <Router>
            <LeanCanvas />
        </Router>
    )
}

export default App
